/**
 * proposalActivityMeta - Metadados visuais dos eventos de PROPOSTA na timeline
 *
 * Fonte única usada pelas timelines de atividades:
 *  - modal de NEGÓCIO (DealActivityTimelinePremium)
 *  - modais de CLIENTE/CONTATO (CustomerActivityTimelinePremium)
 *
 * Os eventos de proposta são gravados pela trigger tg_crm_proposal_deal_activity
 * em crm_deal_activities com activity_type='note' e payload em
 * new_value = { source: 'proposal', event, proposal_number, version_number, total, ... }.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-12 v1.0.0 - Extraído de DealActivityTimelinePremium para reuso
 */

import React from 'react';
import {
  BookOpen,
  CheckCircle2,
  Clock,
  FileText,
  Mail,
  Pencil,
  RotateCcw,
  ScrollText,
  XCircle,
} from 'lucide-react';

export interface ProposalEventMeta {
  /** Ícone semântico do evento */
  icon: React.ComponentType<{ className?: string }>;
  /** Cor do ícone */
  color: string;
  /** Cor da barra lateral do card */
  accent: string;
}

export const PROPOSAL_EVENT_META: Record<string, ProposalEventMeta> = {
  proposal_created:    { icon: ScrollText,   color: 'text-sky-600 dark:text-sky-400',         accent: 'border-l-sky-400' },
  proposal_generated:  { icon: FileText,     color: 'text-sky-600 dark:text-sky-400',         accent: 'border-l-sky-400' },
  proposal_sent:       { icon: Mail,         color: 'text-violet-600 dark:text-violet-400',   accent: 'border-l-violet-400' },
  proposal_viewed:     { icon: BookOpen,     color: 'text-amber-600 dark:text-amber-400',     accent: 'border-l-amber-400' },
  proposal_accepted:   { icon: CheckCircle2, color: 'text-emerald-600 dark:text-emerald-400', accent: 'border-l-emerald-400' },
  proposal_rejected:   { icon: XCircle,      color: 'text-rose-600 dark:text-rose-400',       accent: 'border-l-rose-400' },
  proposal_cancelled:  { icon: XCircle,      color: 'text-rose-600 dark:text-rose-400',       accent: 'border-l-rose-400' },
  proposal_expired:    { icon: Clock,        color: 'text-orange-600 dark:text-orange-400',   accent: 'border-l-orange-400' },
  proposal_superseded: { icon: RotateCcw,    color: 'text-slate-500',                         accent: 'border-l-slate-400' },
  proposal_draft:      { icon: Pencil,       color: 'text-slate-500',                         accent: 'border-l-slate-400' },
  /** Link público gerado/ativado (não confundir com "proposta enviada") */
  proposal_link_generated: { icon: FileText, color: 'text-cyan-600 dark:text-cyan-400',       accent: 'border-l-cyan-400' },
};

/**
 * Resolve o metadado visual de um payload de atividade.
 * Retorna null quando a atividade não é um evento de proposta.
 */
export function resolveProposalEventMeta(
  newValue: Record<string, any> | null | undefined,
): ProposalEventMeta | null {
  if (!newValue || newValue.source !== 'proposal') return null;
  return PROPOSAL_EVENT_META[String(newValue.event)] || PROPOSAL_EVENT_META.proposal_created;
}
